<template>
  <section>
    <v-col>
      <!-- Back button -->
      <v-row class="ma-0 mb-4">
        <v-btn color="secondary" class="text-capitalize" @click="onBack">
          {{ $translate('BACK') }}
        </v-btn>
      </v-row>

      <h2 class="mb-4">{{ $translate('SUBSCRIPTION_STEP4') }}</h2>

      <!-- Name of subscription -->
      <v-row class="mb-3">
        <v-col cols="12" md="6">
          <v-text-field
            v-model="shoppingListName"
            outlined
            hide-details="auto"
            background-color="white"
            :label="$translate('NAME')"
            :rules="requiredRules"
            @change="onNameChange"
          >
            <template v-slot:append-outer>
              <v-tooltip bottom>
                <template v-slot:activator="{ on }">
                  <v-icon v-on="on"> mdi-information-outline </v-icon>
                </template>
                {{ $translate('SUBSCRIPTION_NAME_TOOLTIP') }}
              </v-tooltip>
            </template>
          </v-text-field>
        </v-col>
      </v-row>

      <!-- Products overview -->
      <h3 class="mb-2">{{ $translate('SUBSCRIPTION_PRODUCTS') }}</h3>
      <SubscriptionOverviewProducts :products="products" />

      <!-- Repetition frequency -->
      <v-card flat class="mt-3">
        <v-card-title class="py-2">
          {{ $translate('SUBSCRIPTION_REPETITION_FREQUENCY') }}
        </v-card-title>
        <v-card-text>
          <div>{{ repetitionFrequencyText }}</div>
        </v-card-text>
      </v-card>

      <!-- Delivery info -->
      <SubscriptionOverviewDelivery
        v-if="deliverySelected"
        :event="deliveryPickUpEvent"
        :userInfo="userInfo"
        :startDate="startDate"
        :startTime="startTimeObj.StartTime"
        :endTime="startTimeObj.EndTime"
      />

      <!-- PickUp info -->
      <SubscriptionOverviewPickUp
        v-if="pickUpSelected"
        :event="deliveryPickUpEvent"
        :userInfo="userInfo"
        :startDate="startDate"
        :startTime="startTimeObj.StartTime"
        :endTime="startTimeObj.EndTime"
      />

      <!-- Note -->
      <v-card flat v-if="userInfo.note != null && userInfo.note.trim() != ''">
        <v-card-title class="py-2">{{ $translate('NOTE') }}</v-card-title>
        <v-card-text>
          <div>{{ userInfo.note }}</div>
        </v-card-text>
      </v-card>

      <!-- Show the selected PaymentMethod for this Subscription by the user -->
      <v-card v-if="selectedPaymentMethod.PaymentMethod" class="mt-2" flat>
        <v-col class="pa-0">
          <v-card-title class="py-2">
            {{ $translate('SELECTED_PAYMENT_METHOD') }}
          </v-card-title>
          <v-row align="center" class="mx-4 my-2 pb-4">
            <v-img
              :src="selectedPaymentMethod.PictureUrl"
              max-width="60px"
              max-height="60px"
            />
            <h4 class="text-center ml-6">
              {{ selectedPaymentMethod.DisplayName }}
            </h4>
          </v-row>
        </v-col>
      </v-card>

      <section v-if="showDirectDebitSection">
        <!-- Direct debit status -->
        <section v-if="directDebitActivated">
          <v-card class="mt-4" flat>
            <v-card-text>
              <v-row class="ma-0">
                <v-icon color="green accent-3" class="mr-3">
                  credit_score
                </v-icon>
                <div class="green--text text--accent-3">
                  {{ $translate('DIRECT_DEBIT_ACTIVATED') }}
                </div>
              </v-row>
              <div class="mt-2">
                {{ $translate('CREATE_SUBSCRIPTION_DESC_DIRECT_DEBIT_ON') }}
              </div>
            </v-card-text>
          </v-card>
        </section>
        <section v-else>
          <v-card class="mt-4" flat>
            <v-card-text>
              <v-row class="ma-0">
                <v-icon color="red accent-3" class="mr-3">
                  mdi-credit-card-remove-outline
                </v-icon>
                <div class="red--text text--accent-3">
                  {{ $translate('DIRECT_DEBIT_NOT_ACTIVATED') }}
                </div>
              </v-row>
              <div
                v-html="$translate('CREATE_SUBSCRIPTION_DESC_DIRECT_DEBIT_OFF')"
                class="mt-2"
              />
            </v-card-text>
          </v-card>
        </section>
      </section>

      <!-- Error message -->
      <v-alert
        v-model="showError"
        class="mt-5 mb-2"
        color="red"
        dense
        text
        type="error"
        dismissible
        transition="scale-transition"
      >
        {{ errorMsg }}
      </v-alert>

      <!-- Next button -->
      <v-row class="ma-0 mt-4" justify="end">
        <v-btn
          color="primary"
          class="text-capitalize primary-font-color"
          :loading="saveBtnLoading"
          @click="onNext"
        >
          {{ $translate('SAVE') }}
        </v-btn>
      </v-row>
    </v-col>

    <!-- Dialog to show info before redirecting to mollie for direct debit activation -->
    <ConfirmDialog
      :visible="showInfoDialog"
      :title="$translate('CREATE_SUBSCRIPTION_ACTIVATE_DIRECT_DEBIT_TITLE')"
      :message="$translate('CREATE_SUBSCRIPTION_ACTIVATE_DIRECT_DEBIT_DESC')"
      :positiveBtnText="$translate('CONTINUE')"
      :negativeBtnText="$translate('CANCEL')"
      @positiveBtnClicked="onContinueCreation"
      @negativeBtnClicked="onCancelCreation"
    />

    <!-- Success dialog -->
    <SuccessDialog
      :dialog="successDialog"
      :successTitle="successTitle"
      :successText="successText"
      @continue="continueOnSuccess"
      @back="backOnSuccess"
    />
  </section>
</template>

<script>
import doRequest from '@/api/dssRequest';
import { requiredRules } from '@/shared/validations_rules';
import SubscriptionOverviewProducts from '@/components/subscription/SubscriptionOverviewProducts';
import SubscriptionOverviewDelivery from '@/components/subscription/SubscriptionOverviewDelivery';
import SubscriptionOverviewPickUp from '@/components/subscription/SubscriptionOverviewPickUp';
import ConfirmDialog from '@/components/generalComponents/uiComponents/ConfirmDialog';
import SuccessDialog from '@/components/generalComponents/uiComponents/SuccessDialog';
import PaymentMethod from '@/objects/PaymentMethod';

export default {
  name: 'SubscriptionOverview',
  components: {
    SubscriptionOverviewProducts,
    SubscriptionOverviewDelivery,
    SubscriptionOverviewPickUp,
    ConfirmDialog,
    SuccessDialog,
  },
  data() {
    return {
      shoppingListName: '',
      requiredRules,
      saveBtnLoading: false,
      showError: false,
      errorMsg: '',
      showInfoDialog: false,
      paymentUrl: '',
      successTitle: this.$translate('CREATE_SUBSCRIPTION_SUCCESS_TITLE'),
      successText: '',
      successDialog: false,
    };
  },
  computed: {
    userObjectId() {
      return this.$store.getters['auth/userObjectId'];
    },
    products() {
      return this.$store.getters['subscription/products'];
    },
    subscriptionProducts() {
      const productsTmp = [];

      this.products.forEach((product) => {
        productsTmp.push({
          ProductId: product.ProductId,
          ItemId: product.ItemId,
          Quantity: product.Quantity,
        });
      });
    },
    deliveryPickUpEvent() {
      return this.$store.getters['subscription/deliveryPickUpEvent'];
    },
    deliveryPickUpOrderId() {
      return this.$store.getters['subscription/deliveryPickUpOrderId'];
    },
    userInfo() {
      return this.$store.getters['subscription/userInfo'];
    },
    startTimeObj() {
      return this.$store.getters['subscription/startTimeObj'];
    },
    startDate() {
      return this.$store.getters['subscription/startDate'];
    },
    repetitionFrequency() {
      return this.$store.getters['subscription/repetitionFrequency'];
    },
    repetitionFrequencyText() {
      return this.getRepetitionFrequencyText();
    },
    selectedPaymentMethod() {
      return this.$store.getters['subscription/selectedPaymentMethod'];
    },
    payloadProducts() {
      return this.getSelectedProducts();
    },
    deliverySelected() {
      if (
        this.deliveryPickUpEvent != null &&
        (this.deliveryPickUpEvent.Type === 'Delivery' ||
          this.deliveryPickUpEvent.Type === 'Shiplee')
      ) {
        return true;
      }
      return false;
    },
    pickUpSelected() {
      if (
        this.deliveryPickUpEvent != null &&
        this.deliveryPickUpEvent.Type === 'PickUp'
      ) {
        return true;
      }
      return false;
    },
    directDebitActivated() {
      const userObj = this.$store.getters['auth/user'];
      if (userObj != null && userObj.allowDirectDebit) {
        return true;
      }
      return false;
    },
    buyByAccountCompletelyConfigured() {
      return this.$store.getters['auth/buyByAccountCompletelyConfigured'];
    },
    showDirectDebitSection() {
      if (
        this.buyByAccountCompletelyConfigured ||
        this.selectedPaymentMethod.PaymentMethod !== PaymentMethod.DIRECT_DEBIT
      ) {
        // Subscription will be handled by BuyByAccount instead of direct debit.
        // Direct debit section is hidden for this case
        return false;
      }
      return true;
    },
  },
  mounted() {
    this.initName();
  },
  methods: {
    initName() {
      /*
      Create default subscription name by prepending either partner group name
      or event name before [Subscription]
      */
      if (this.$route.params.event_id == null) {
        // Subscription for partner group
        this.initNameForPg();
      } else {
        // Subscription for event
        this.initNameForEvent();
      }

      this.addSuffixToName();
    },
    initNameForPg() {
      const defaultName = this.$translate('SUBSCRIPTION');
      const pgInfo = this.$store.getters['Account/partnerGroupDetails'];
      this.shoppingListName = `${pgInfo.PartnerGroupDisplayName} - ${defaultName}`;
    },
    initNameForEvent() {
      const defaultName = this.$translate('SUBSCRIPTION');
      const eventInfo = this.$store.getters['subscription/event'];
      this.shoppingListName = `${eventInfo.EventTranslationName} - ${defaultName}`;
    },
    addSuffixToName() {
      // Add a suffix to current name if it's already available
      const shoppingLists = this.$store.getters['shoppingList/orders'];
      if (shoppingLists == null || shoppingLists.length === 0) {
        return;
      }

      const subscriptions = shoppingLists.filter(
        (list) => list.Type === 'Subscription'
      );
      if (subscriptions.length === 0) {
        return;
      }

      let exist = true;
      let suffix = 0;
      const maxIteration = 50;
      const initialName = this.shoppingListName;
      while (exist) {
        if (suffix > 0) {
          this.shoppingListName = `${initialName} ${suffix}`;
        }

        const itemsWithSameName = subscriptions.filter(
          (list) => list.ShoppingListName === this.shoppingListName
        );

        if (itemsWithSameName.length === 0 || suffix > maxIteration) {
          exist = false;
        }

        suffix += 1;
      }
    },
    onNameChange() {
      this.$store.commit(
        'subscription/setShoppingListName',
        this.shoppingListName
      );
    },
    getSelectedProducts() {
      const productsInStore = this.$store.getters['subscription/products'];
      if (productsInStore == null) {
        return [];
      }

      const productsTmp = [];

      productsInStore.forEach((product) => {
        if (product != null && product.ItemId != null) {
          productsTmp.push({
            ProductId: product.ProductId,
            ItemId: product.ItemId,
            Quantity: product.Quantity,
            OptionSelection: product.OptionSelection,
          });
        }
      });

      return productsTmp;
    },
    getRepetitionFrequencyText() {
      switch (this.repetitionFrequency) {
        case 1:
          return this.$translate('EVERY_WEEK');
        case 2:
          return this.$translate('EVERY_TWO_WEEKS');
        case 3:
          return this.$translate('EVERY_THREE_WEEKS');
        case 4:
          return this.$translate('EVERY_FOUR_WEEKS');
        default:
          return '';
      }
    },
    createSubscription() {
      const payload = {
        Name: this.shoppingListName,
        SubscriptionProducts: this.payloadProducts,
        SubEventId: this.deliveryPickUpEvent.EventId,
        OrderId: this.deliveryPickUpOrderId,
        Frequency: this.repetitionFrequency,
        PaymentMethod: this.selectedPaymentMethod,
      };

      doRequest(
        (response) => {
          this.saveBtnLoading = false;

          if (response.data.ShoppingList != null) {
            this.$store.commit(
              'setCurrentSelectedShoppingList',
              response.data.ShoppingList
            );
          }

          if (
            response.data.PaymentUrl != null &&
            response.data.PaymentUrl.trim() !== ''
          ) {
            // Need a payment to activate direct debit
            window.location.href = response.data.PaymentUrl;
          } else {
            if (response.data.PaymentMessage != null) {
              this.successText = response.data.PaymentMessage;
            } else {
              this.successText = this.$translate('CREATE_SUBSCRIPTION_SUCCESS');
            }

            this.successDialog = true;
            // this.$emit('next');
            // this.clearSubscription();
          }
        },
        (error) => {
          this.saveBtnLoading = false;

          if (error.data != null && error.data.ErrorCode === 'E_PO_006') {
            this.errorMsg = this.$translate('E_PO_006_SUBSCRIPTION_PRODUCT');
          } else {
            this.errorMsg = this.$translate('CREATE_SUBSCRIPTION_ERROR');
          }

          this.showError = true;
        },
        'createSubscription',
        this.userObjectId,
        payload
      );
    },
    clearSubscription() {
      // Clear current data saved in subscription store module
      this.$store.commit('subscription/clear');
    },
    onContinueCreation() {
      // Redirect to mollie payment page to activate direct debit
      this.saveBtnLoading = true;
      this.showInfoDialog = false;
      this.createSubscription();
    },
    onCancelCreation() {
      this.showInfoDialog = false;
    },
    onBack() {
      this.$emit('back');
    },
    onNext() {
      this.showError = false;
      this.errorMsg = '';

      if (this.buyByAccountCompletelyConfigured || this.directDebitActivated) {
        this.saveBtnLoading = true;
        this.createSubscription();
      } else {
        /*
        Show a dialog to inform user that he will be redirected to mollie payment screen
        for direct debit activation
        */
        this.showInfoDialog = true;
      }
    },
    continueOnSuccess() {
      this.$emit('next');
      this.clearSubscription();
      window.EventBus.fire('openShoppingListDialog', true);
      this.successDialog = false;
    },
    backOnSuccess() {
      this.$emit('next');
      this.clearSubscription();
      this.successDialog = false;
    },
  },
};
</script>
